/**
 * ProcurementDailyReport.jsx — Iter 117
 * Live-aggregated daily snapshot of the entire procurement pipeline.
 * Mirrors the user's "Daily Report Format for Purchase.xlsx".
 *
 * Data source: GET /api/procurement/daily-report?date=YYYY-MM-DD
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  ShoppingCart, FileText, ClipboardList, TrendingDown, Clock,
  AlertTriangle, IndianRupee, ShieldAlert, Copy, Save, Download, Scale,
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { apiGet, apiPut } from '../lib/api';

function Card({ title, icon, children, count, action }) {
  return (
    <section className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
      <header className="flex items-start justify-between gap-3 pb-2 border-b border-slate-100">
        <div className="flex items-start gap-2.5">
          <div className="p-1.5 rounded-md bg-[#1F7A6E]/10 text-[#1F7A6E]">{icon}</div>
          <div>
            <h3 className="font-display font-bold uppercase tracking-wide text-xs text-slate-800">{title}</h3>
            {count != null && <div className="text-[10px] text-slate-500 mt-0.5">{count} record{count === 1 ? '' : 's'}</div>}
          </div>
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

function KpiTile({ label, value, accent, suffix }) {
  return (
    <div className={`rounded-md border ${accent || 'border-slate-200'} bg-white px-3 py-2.5`}>
      <div className="text-[9px] uppercase tracking-widest text-slate-500 font-display font-bold">{label}</div>
      <div className="font-display font-bold text-xl mt-0.5 text-slate-800 tabular-nums">{value}{suffix && <span className="text-xs text-slate-400 ml-1">{suffix}</span>}</div>
    </div>
  );
}

function EmptyRow({ msg = 'No records' }) {
  return <div className="text-xs text-slate-400 italic py-3 text-center">{msg}</div>;
}

const fmtINR = (v) => {
  if (v == null) return '—';
  return `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

export default function ProcurementDailyReport() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [shortage, setShortage] = useState({ critical_shortages: '', impact_on_execution: '', escalation_required: 'No', escalation_to: '' });
  const [savingShortage, setSavingShortage] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiGet(`/procurement/daily-report?date=${date}`);
      setData(r);
      setNotes(r?.sections?.risks_notes || '');
      setShortage(r?.sections?.shortage_impact || { critical_shortages: '', impact_on_execution: '', escalation_required: 'No', escalation_to: '' });
    } catch (e) {
      toast.error('Could not load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const s = data?.sections || {};
  const k = s.kpis || {};

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await apiPut('/procurement/daily-report/notes', { date, notes });
      toast.success('Notes saved');
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSavingNotes(false);
    }
  };

  const saveShortage = async () => {
    setSavingShortage(true);
    try {
      await apiPut('/procurement/daily-report/notes', { date, shortage_impact: shortage });
      toast.success('Shortage impact saved');
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSavingShortage(false);
    }
  };

  const copyWhatsApp = async () => {
    try {
      await navigator.clipboard.writeText(s.whatsapp_summary || '');
      toast.success('WhatsApp summary copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  const downloadCsv = () => {
    if (!data) return;
    const lines = [];
    const push = (title, rows, cols) => {
      lines.push('', title);
      lines.push(cols.join(','));
      rows.forEach(r => lines.push(cols.map(c => `"${(r[c] ?? '').toString().replace(/"/g, '""')}"`).join(',')));
    };
    push('REQUIREMENT SUMMARY', s.requirement_summary || [], ['mpr_no', 'project', 'items', 'estimated_value', 'status']);
    push('OPEN RFQs', s.rfq_status?.open_rfqs || [], ['rfq_no', 'project', 'vendors_invited', 'responses', 'due_date']);
    push('POs TODAY', s.po_status?.today || [], ['po_no', 'vendor', 'value', 'status']);
    push('POs PENDING APPROVAL', s.po_status?.pending_approval || [], ['po_no', 'vendor', 'value', 'required_approver']);
    push('DELIVERIES TODAY', s.deliveries_today || [], ['grn_no', 'po_no', 'vendor', 'items_received', 'value']);
    push('OVERDUE POs', s.shortage_delay || [], ['po_no', 'vendor', 'expected_delivery', 'days_overdue', 'value']);
    push('VENDOR FOLLOWUPS', s.vendor_followups || [], ['rfq_no', 'project', 'invited', 'responded', 'pending', 'age_days']);
    push('HIGH-VALUE PURCHASES', s.high_value_purchases || [], ['po_no', 'vendor', 'value', 'approver', 'approval_tier']);
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `procurement-daily-${date}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="space-y-5 pb-10">
      <PageHeader
        title="Procurement Daily Report"
        subtitle="Live snapshot of indents, RFQs, POs, deliveries, KPIs and compliance — auto-aggregated from the live procurement pipeline."
        right={
          <div className="flex items-center gap-2">
            <input
              type="date" value={date} onChange={(e) => setDate(e.target.value)}
              max={today}
              className="border border-slate-200 rounded-md px-3 py-1.5 text-xs"
              data-testid="pdr-date"
            />
            <button onClick={copyWhatsApp} data-testid="pdr-copy-whatsapp" className="px-3 py-1.5 text-xs rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 flex items-center gap-1.5">
              <Copy size={12} /> WhatsApp
            </button>
            <button onClick={downloadCsv} data-testid="pdr-export" className="px-3 py-1.5 text-xs rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 flex items-center gap-1.5">
              <Download size={12} /> CSV
            </button>
          </div>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="pdr-kpis">
        <KpiTile label="Pending MRs" value={k.pending_mr ?? k.indents_open ?? 0} />
        <KpiTile label="RFQs open" value={k.rfqs_open ?? 0} />
        <KpiTile label="POs today" value={k.pos_raised_today ?? 0} />
        <KpiTile label="PO value today" value={fmtINR(k.po_value_today_inr)} />
        <KpiTile label="Pending approval" value={k.pos_pending_approval ?? 0} accent={k.pos_pending_approval ? 'border-amber-300' : ''} />
        <KpiTile label="Deliveries today" value={k.deliveries_today ?? 0} />
        <KpiTile label="Overdue POs" value={k.overdue_pos ?? 0} accent={k.overdue_pos ? 'border-rose-300' : ''} />
        <KpiTile label="Vendor follow-ups" value={k.vendor_followups ?? 0} accent={k.vendor_followups ? 'border-amber-300' : ''} />
        <KpiTile label="High-value (≥₹50k)" value={k.high_value_purchases_today ?? 0} />
        <KpiTile label="Non-compliant vendors" value={k.non_compliant_vendors ?? 0} accent={k.non_compliant_vendors ? 'border-rose-300' : ''} />
      </div>

      {/* Sections */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="Purchase Requirement Summary (MPRs)" icon={<ClipboardList size={14} />} count={(s.requirement_summary || []).length}>
          {(s.requirement_summary || []).length === 0 ? <EmptyRow /> : (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <tr><th className="text-left py-1">MPR</th><th className="text-left">Project</th><th className="text-right">Items</th><th className="text-right">Value</th><th>Status</th></tr>
              </thead>
              <tbody>
                {(s.requirement_summary || []).slice(0, 20).map((r, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-1 font-mono">{r.mpr_no}</td>
                    <td>{r.project}</td>
                    <td className="text-right">{r.items}</td>
                    <td className="text-right tabular-nums">{fmtINR(r.estimated_value)}</td>
                    <td className="text-center"><span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100">{r.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Open RFQs" icon={<FileText size={14} />} count={(s.rfq_status?.open_rfqs || []).length}>
          {(s.rfq_status?.open_rfqs || []).length === 0 ? <EmptyRow /> : (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100"><tr><th className="text-left py-1">RFQ</th><th className="text-left">Project</th><th className="text-right">Invited</th><th className="text-right">Resp</th><th>Due</th></tr></thead>
              <tbody>
                {(s.rfq_status?.open_rfqs || []).slice(0, 20).map((r, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-1 font-mono">{r.rfq_no}</td>
                    <td>{r.project}</td>
                    <td className="text-right">{r.vendors_invited}</td>
                    <td className="text-right">{r.responses}</td>
                    <td className="text-center">{r.due_date || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="POs Pending Approval" icon={<Clock size={14} />} count={(s.po_status?.pending_approval || []).length}>
          {(s.po_status?.pending_approval || []).length === 0 ? <EmptyRow /> : (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100"><tr><th className="text-left py-1">PO</th><th className="text-left">Vendor</th><th className="text-right">Value</th><th className="text-left">Needs</th></tr></thead>
              <tbody>
                {(s.po_status?.pending_approval || []).slice(0, 20).map((r, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-1 font-mono">{r.po_no}</td><td>{r.vendor}</td>
                    <td className="text-right tabular-nums">{fmtINR(r.value)}</td>
                    <td className="text-amber-700 text-[11px]">{r.required_approver}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Deliveries Received Today" icon={<ShoppingCart size={14} />} count={(s.deliveries_today || []).length}>
          {(s.deliveries_today || []).length === 0 ? <EmptyRow /> : (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100"><tr><th className="text-left py-1">GRN</th><th className="text-left">PO</th><th className="text-left">Vendor</th><th className="text-right">Items</th><th className="text-right">Value</th></tr></thead>
              <tbody>
                {(s.deliveries_today || []).slice(0, 20).map((r, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-1 font-mono">{r.grn_no}</td><td className="font-mono">{r.po_no || '—'}</td>
                    <td>{r.vendor}</td><td className="text-right">{r.items_received}</td>
                    <td className="text-right tabular-nums">{fmtINR(r.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Overdue / Delayed POs" icon={<TrendingDown size={14} />} count={(s.shortage_delay || []).length}>
          {(s.shortage_delay || []).length === 0 ? <EmptyRow msg="No overdue deliveries" /> : (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100"><tr><th className="text-left py-1">PO</th><th className="text-left">Vendor</th><th>Expected</th><th className="text-right">Days late</th><th className="text-right">Value</th></tr></thead>
              <tbody>
                {(s.shortage_delay || []).slice(0, 20).map((r, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-1 font-mono">{r.po_no}</td><td>{r.vendor}</td>
                    <td className="text-center">{r.expected_delivery}</td>
                    <td className="text-right text-rose-700 font-bold">{r.days_overdue}</td>
                    <td className="text-right tabular-nums">{fmtINR(r.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Vendor Follow-ups (RFQs > 3 days)" icon={<AlertTriangle size={14} />} count={(s.vendor_followups || []).length}>
          {(s.vendor_followups || []).length === 0 ? <EmptyRow msg="All RFQs in good standing" /> : (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100"><tr><th className="text-left py-1">RFQ</th><th className="text-left">Project</th><th className="text-right">Inv</th><th className="text-right">Resp</th><th className="text-right">Pending</th><th className="text-right">Age</th></tr></thead>
              <tbody>
                {(s.vendor_followups || []).slice(0, 20).map((r, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-1 font-mono">{r.rfq_no}</td><td>{r.project}</td>
                    <td className="text-right">{r.invited}</td><td className="text-right">{r.responded}</td>
                    <td className="text-right text-amber-700 font-bold">{r.pending}</td>
                    <td className="text-right">{r.age_days}d</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="High-Value Purchases (≥ ₹50,000)" icon={<IndianRupee size={14} />} count={(s.high_value_purchases || []).length}>
          {(s.high_value_purchases || []).length === 0 ? <EmptyRow /> : (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100"><tr><th className="text-left py-1">PO</th><th className="text-left">Vendor</th><th className="text-right">Value</th><th className="text-left">Approver</th></tr></thead>
              <tbody>
                {(s.high_value_purchases || []).slice(0, 20).map((r, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-1 font-mono">{r.po_no}</td><td>{r.vendor}</td>
                    <td className="text-right tabular-nums font-bold">{fmtINR(r.value)}</td>
                    <td>{r.approver || '—'} {r.approval_tier && <span className="text-[10px] text-slate-500 ml-1">({r.approval_tier})</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Statutory Compliance" icon={<ShieldAlert size={14} />} count={(s.non_compliant_vendors || []).length}>
          {(s.non_compliant_vendors || []).length === 0 ? <EmptyRow msg="All vendors GST + PAN compliant" /> : (
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100"><tr><th className="text-left py-1">Vendor</th><th className="text-left">Missing</th></tr></thead>
              <tbody>
                {(s.non_compliant_vendors || []).slice(0, 20).map((r, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-1">{r.vendor}</td>
                    <td className="text-rose-600 uppercase font-bold">{(r.missing || []).join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Rate Comparison (Today's POs)" icon={<Scale size={14} />} count={(s.rate_comparisons || []).length}>
          {(s.rate_comparisons || []).length === 0 ? <EmptyRow msg="No rate-compared POs today" /> : (
            <table className="w-full text-xs" data-testid="pdr-rate-comparison">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="text-left py-1">PO</th>
                  <th className="text-left">Awarded</th>
                  <th className="text-right">L1</th>
                  <th className="text-right">Highest</th>
                  <th className="text-right">Vendors</th>
                  <th className="text-right">Saved vs H</th>
                </tr>
              </thead>
              <tbody>
                {(s.rate_comparisons || []).slice(0, 20).map((r, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-1 font-mono">{r.po_no || '—'}</td>
                    <td>{r.awarded_vendor || '—'}</td>
                    <td className="text-right tabular-nums">{fmtINR(r.lowest)}</td>
                    <td className="text-right tabular-nums text-slate-500">{fmtINR(r.highest)}</td>
                    <td className="text-right">{r.vendors_compared}</td>
                    <td className="text-right tabular-nums font-bold text-emerald-700">{fmtINR(r.savings_vs_highest)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      {/* Shortage Impact + Risks/Notes + WhatsApp */}
      <section className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid="pdr-shortage-impact">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber-600" />
            <h3 className="font-display font-bold uppercase tracking-wide text-xs text-slate-800">Shortage / Delay Impact on Project</h3>
          </div>
          <button onClick={saveShortage} disabled={savingShortage} data-testid="pdr-save-shortage" className="text-xs px-3 py-1.5 bg-[#1F7A6E] text-white rounded-md hover:bg-[#14504E] disabled:opacity-50 flex items-center gap-1.5">
            <Save size={11} /> {savingShortage ? 'Saving…' : 'Save'}
          </button>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">Critical Shortages Today</label>
            <textarea
              rows={2}
              value={shortage.critical_shortages || ''}
              onChange={(e) => setShortage(s => ({ ...s, critical_shortages: e.target.value }))}
              placeholder="e.g. Steel Bars, Cement (40 bags), Ready-mix"
              data-testid="pdr-shortage-critical"
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]"
            />
          </div>
          <div>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">Impact on Site Execution</label>
            <textarea
              rows={2}
              value={shortage.impact_on_execution || ''}
              onChange={(e) => setShortage(s => ({ ...s, impact_on_execution: e.target.value }))}
              placeholder="e.g. Shuttering delayed · concreting deferred · 25 mandays idle"
              data-testid="pdr-shortage-impact-text"
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]"
            />
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">Escalation Required?</label>
            <select
              value={shortage.escalation_required || 'No'}
              onChange={(e) => setShortage(s => ({ ...s, escalation_required: e.target.value }))}
              data-testid="pdr-shortage-esc-req"
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
            >
              <option>No</option>
              <option>Yes</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">If Yes → To Whom</label>
            <input
              value={shortage.escalation_to || ''}
              onChange={(e) => setShortage(s => ({ ...s, escalation_to: e.target.value }))}
              placeholder="e.g. Procurement Head · CFO · MD"
              data-testid="pdr-shortage-esc-to"
              disabled={shortage.escalation_required !== 'Yes'}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-400"
            />
          </div>
        </div>
      </section>

      {/* Risks/Notes + WhatsApp */}
      <div className="grid lg:grid-cols-2 gap-4">
        <section className="bg-white border border-slate-200 rounded-md p-4 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-bold uppercase tracking-wide text-xs text-slate-800">Risks · Issues · CEO Notes</h3>
            <button onClick={saveNotes} disabled={savingNotes} data-testid="pdr-save-notes" className="text-xs px-3 py-1.5 bg-[#1F7A6E] text-white rounded-md hover:bg-[#14504E] disabled:opacity-50 flex items-center gap-1.5">
              <Save size={11} /> {savingNotes ? 'Saving…' : 'Save'}
            </button>
          </div>
          <textarea
            rows={6}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Today's procurement risks, vendor escalations, supply chain blockers, CEO notes…"
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]"
            data-testid="pdr-notes"
          />
        </section>

        <section className="bg-emerald-50/40 border border-emerald-200 rounded-md p-4 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-bold uppercase tracking-wide text-xs text-emerald-800">WhatsApp Summary (8-10 lines)</h3>
            <button onClick={copyWhatsApp} className="text-xs px-3 py-1.5 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 flex items-center gap-1.5">
              <Copy size={11} /> Copy
            </button>
          </div>
          <pre className="whitespace-pre-wrap font-mono text-xs text-slate-700 bg-white border border-emerald-200/60 rounded-md p-3" data-testid="pdr-whatsapp">{s.whatsapp_summary || '—'}</pre>
        </section>
      </div>
    </div>
  );
}
